import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";

import { uuidv4 } from "utils/uuid";

const referenceSuggestionPluginKey = new PluginKey("note-reference-citation-suggestion");

function getReferenceTitle(reference) {
    const title = reference?.bib_json?.title || reference?.reference_bib_json?.title;
    if (Array.isArray(title)) return title.filter(Boolean).join(" ");
    return title || reference?.display_name || reference?.reference_display_name || "제목 없음";
}

function getReferenceAuthors(reference) {
    const bibJson = reference?.bib_json || reference?.reference_bib_json || {};
    const authors = bibJson.author;
    if (!Array.isArray(authors)) return "";
    return authors
        .map((author) => author.literal || [author.family, author.given].filter(Boolean).join(" "))
        .filter(Boolean)
        .join(" and ");
}

function getReferenceYear(reference) {
    const bibJson = reference?.bib_json || reference?.reference_bib_json || {};
    return bibJson.year || bibJson.issued?.["date-parts"]?.[0]?.[0] || "";
}

function createElement(tagName, textContent = "") {
    const element = document.createElement(tagName);
    if (textContent) element.textContent = textContent;
    return element;
}

function styleContainer(container) {
    Object.assign(container.style, {
        position: "fixed",
        zIndex: "1100",
        width: "320px",
        maxWidth: "calc(100vw - 24px)",
        maxHeight: "280px",
        overflowY: "auto",
        padding: "6px",
        border: "1px solid var(--color-Line)",
        borderRadius: "6px",
        background: "var(--color-White)",
        boxShadow: "0 14px 36px rgba(15, 23, 42, 0.16)",
    });
}

function styleButton(button, isSelected) {
    Object.assign(button.style, {
        display: "grid",
        width: "100%",
        padding: "8px 10px",
        border: "0",
        borderRadius: "4px",
        background: isSelected ? "var(--color-Base1)" : "transparent",
        color: "var(--color-SubBlack)",
        cursor: "pointer",
        textAlign: "left",
    });
}

function scrollSelectedItemIntoView(container) {
    const selectedItem = container?.querySelector?.("[data-reference-suggestion-selected='true']");
    selectedItem?.scrollIntoView?.({ block: "nearest" });
}

function positionContainer(container, view, range) {
    if (!container || !view || !range) return;
    let rect = null;
    try {
        const coords = view.coordsAtPos(range.from);
        rect = {
            top: coords.top,
            right: coords.right,
            bottom: coords.bottom,
            left: coords.left,
        };
    } catch (_error) {
        return;
    }

    const margin = 8;
    const containerRect = container.getBoundingClientRect();
    const width = containerRect.width || 320;
    const height = containerRect.height || 48;
    const left = Math.min(Math.max(margin, rect.left), Math.max(margin, window.innerWidth - width - margin));
    const belowTop = rect.bottom + 6;
    const aboveTop = rect.top - height - 6;
    const top = belowTop + height <= window.innerHeight - margin ? belowTop : Math.max(margin, aboveTop);
    container.style.left = `${left}px`;
    container.style.top = `${top}px`;
}

function findReferenceTrigger(state) {
    const { selection } = state;
    if (!selection.empty) return null;
    const { $from } = selection;
    if ($from.parent.type.spec.code) return null;

    const textBeforeCursor = $from.parent.textBetween(0, $from.parentOffset, "\n", "\n");
    const triggerIndex = textBeforeCursor.lastIndexOf("#");
    if (triggerIndex < 0) return null;

    const previousChar = triggerIndex > 0 ? textBeforeCursor[triggerIndex - 1] : "";
    if (previousChar && !/[\s([{]/.test(previousChar)) return null;

    const query = textBeforeCursor.slice(triggerIndex + 1);
    if (query.startsWith(" ") || query.includes("\n")) return null;

    const from = $from.start() + triggerIndex;
    return {
        from,
        to: selection.from,
        query,
    };
}

function createReferenceSuggestionView({ editor, onInserted, searchReferences }) {
    let container = null;
    let currentRange = null;
    let currentQuery = null;
    let currentView = null;
    let items = [];
    let selectedIndex = 0;
    let requestSeq = 0;
    let composing = false;

    const ensureContainer = () => {
        if (container) return;
        container = document.createElement("div");
        container.setAttribute("role", "listbox");
        container.setAttribute("aria-label", "참고문헌 자동완성");
        styleContainer(container);
        document.body.appendChild(container);
    };

    const close = () => {
        requestSeq += 1;
        currentRange = null;
        currentQuery = null;
        items = [];
        selectedIndex = 0;
        container?.remove();
        container = null;
    };

    const render = () => {
        if (!container || !currentView || !currentRange) return;
        container.innerHTML = "";

        if (!items.length) {
            const empty = createElement("div", "참고문헌 없음");
            Object.assign(empty.style, {
                padding: "10px",
                color: "var(--color-Grey1)",
                fontSize: "1.3rem",
            });
            container.appendChild(empty);
            positionContainer(container, currentView, currentRange);
            return;
        }

        items.forEach((reference, index) => {
            const title = getReferenceTitle(reference);
            const meta = [
                reference.reference_no ? `#${reference.reference_no}` : null,
                getReferenceAuthors(reference),
                getReferenceYear(reference),
            ]
                .filter(Boolean)
                .join(" · ");
            const button = createElement("button");
            button.type = "button";
            button.setAttribute("aria-label", `${title} 인용 삽입`);
            button.setAttribute("data-reference-suggestion-selected", String(index === selectedIndex));
            styleButton(button, index === selectedIndex);

            const titleElement = createElement("div", title);
            Object.assign(titleElement.style, {
                minWidth: "0",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontSize: "1.35rem",
                lineHeight: "1.45",
                color: "var(--color-SubBlack)",
            });
            button.appendChild(titleElement);

            if (meta) {
                const metaElement = createElement("div", meta);
                Object.assign(metaElement.style, {
                    minWidth: "0",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    fontSize: "1.15rem",
                    lineHeight: "1.4",
                    color: "var(--color-Grey1)",
                });
                button.appendChild(metaElement);
            }

            button.onmouseenter = () => {
                if (selectedIndex === index) return;
                selectedIndex = index;
                render();
            };
            button.onmousedown = (event) => {
                event.preventDefault();
                event.stopPropagation();
                insertReference(reference);
            };
            container.appendChild(button);
        });

        positionContainer(container, currentView, currentRange);
        scrollSelectedItemIntoView(container);
    };

    const search = async (query) => {
        const seq = requestSeq + 1;
        requestSeq = seq;
        if (typeof searchReferences !== "function") {
            items = [];
            render();
            return;
        }
        try {
            const nextItems = await searchReferences(query);
            if (requestSeq !== seq) return;
            items = Array.isArray(nextItems) ? nextItems : [];
            selectedIndex = 0;
            render();
        } catch (_error) {
            if (requestSeq !== seq) return;
            items = [];
            render();
        }
    };

    const updateFromView = (view, { forceSearch = false } = {}) => {
        currentView = view;
        const range = findReferenceTrigger(view.state);
        if (!range) {
            close();
            return;
        }

        ensureContainer();
        const queryChanged = currentQuery !== range.query;
        currentRange = range;
        currentQuery = range.query;
        render();

        if (!composing && (queryChanged || forceSearch)) {
            search(range.query.trim());
        }
    };

    const insertReference = (reference) => {
        if (!reference?.id || !currentRange) return;
        const insertPosition = currentRange.from;
        const occurrenceId = uuidv4();
        editor
            .chain()
            .focus()
            .insertContentAt(currentRange, {
                type: "referenceCitation",
                attrs: {
                    occurrenceId,
                    referenceId: reference.id,
                    referenceNo: reference.reference_no,
                },
            })
            .setTextSelection(insertPosition + 1)
            .run();
        close();
        onInserted?.(editor, insertPosition);
    };

    const handleKeyDown = (event) => {
        if (!container || !currentRange) return;
        if (event.isComposing || event.keyCode === 229 || composing) return;

        if (event.key === "ArrowDown") {
            event.preventDefault();
            event.stopPropagation();
            selectedIndex = items.length ? (selectedIndex + 1) % items.length : 0;
            render();
            return;
        }
        if (event.key === "ArrowUp") {
            event.preventDefault();
            event.stopPropagation();
            selectedIndex = items.length ? (selectedIndex + items.length - 1) % items.length : 0;
            render();
            return;
        }
        if (event.key === "Escape") {
            event.preventDefault();
            event.stopPropagation();
            close();
            return;
        }
        if (event.key === "Enter" && items[selectedIndex]) {
            event.preventDefault();
            event.stopPropagation();
            insertReference(items[selectedIndex]);
        }
    };

    const handleCompositionStart = () => {
        composing = true;
    };

    const handleCompositionEnd = () => {
        composing = false;
        window.setTimeout(() => {
            if (currentView) updateFromView(currentView, { forceSearch: true });
        }, 0);
    };

    const handleViewportChange = () => {
        if (container && currentView && currentRange) {
            positionContainer(container, currentView, currentRange);
        }
    };

    return {
        update: (view) => updateFromView(view),
        destroy: () => {
            close();
            viewCleanup();
        },
        bind: (view) => {
            currentView = view;
            view.dom.addEventListener("keydown", handleKeyDown, true);
            view.dom.addEventListener("compositionstart", handleCompositionStart);
            view.dom.addEventListener("compositionend", handleCompositionEnd);
            window.addEventListener("resize", handleViewportChange);
            window.addEventListener("scroll", handleViewportChange, true);
        },
        unbind: (view) => {
            view.dom.removeEventListener("keydown", handleKeyDown, true);
            view.dom.removeEventListener("compositionstart", handleCompositionStart);
            view.dom.removeEventListener("compositionend", handleCompositionEnd);
            window.removeEventListener("resize", handleViewportChange);
            window.removeEventListener("scroll", handleViewportChange, true);
        },
    };

    function viewCleanup() {
        if (!currentView) return;
        const view = currentView;
        currentView = null;
        view.dom.removeEventListener("keydown", handleKeyDown, true);
        view.dom.removeEventListener("compositionstart", handleCompositionStart);
        view.dom.removeEventListener("compositionend", handleCompositionEnd);
        window.removeEventListener("resize", handleViewportChange);
        window.removeEventListener("scroll", handleViewportChange, true);
    }
}

const ReferenceCitationSuggestion = Extension.create({
    name: "noteReferenceCitationSuggestion",

    addOptions() {
        return {
            onInserted: null,
            searchReferences: null,
        };
    },

    addProseMirrorPlugins() {
        const { onInserted, searchReferences } = this.options;
        const editor = this.editor;

        return [
            new Plugin({
                key: referenceSuggestionPluginKey,
                view(view) {
                    const suggestionView = createReferenceSuggestionView({
                        editor,
                        onInserted,
                        searchReferences,
                    });
                    suggestionView.bind(view);
                    suggestionView.update(view);
                    return {
                        update: (nextView) => suggestionView.update(nextView),
                        destroy: () => {
                            suggestionView.unbind(view);
                            suggestionView.destroy();
                        },
                    };
                },
            }),
        ];
    },
});

export default ReferenceCitationSuggestion;
